import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Row,
} from "reactstrap";
import { toast } from "react-toastify";
import Select from "react-select";
import Swal from "sweetalert2";

import CommonTable from "components/Common/TableContainer";
import Loaders from "components/Common/Loaders";
import { AsyncClientSelect } from "components/Common/AsyncSearchSelects";
import {
    applyClientAsyncFilterOptionToFormState,
    buildClientAsyncFilterInitialOption,
    getClientAsyncFilterLabelFieldKey,
    normalizeMetadataFilterDateForApi,
} from "helpers/metadataFilterHelpers";
import dayjs from "dayjs";
import { phoneNumberShowFormat } from "helpers/GeneralHelper";
import {
    fetchReportMailSourceOptions,
    mapReportMailSourceOptions,
} from "model/InProgressReportModel";
import {
    submitRtrInternalReportFilterData,
    submitRtrInternalReportScheduleData,
} from "model/RtrInternalReportModel";

const FEDEX_SECTION_KEYS = new Set([
    "fedexdriver",
    "fedex-driver",
    "fedexdriveritems",
    "fedex",
]);

const normalizeSectionKey = (value = "") =>
    String(value ?? "")
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, "");

const resolveSectionBucket = (sectionKey = "") => {
    const normalized = normalizeSectionKey(sectionKey);
    if (FEDEX_SECTION_KEYS.has(normalized)) return "fedexDriver";
    return "eligible";
};

const DEFAULT_SCHEDULE_SUBMIT_TYPE = "Schedule Mail";

const selectMenuStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
};

const TABLE_COLUMNS = [
    { Header: "Name", accessor: "name" },
    { Header: "Fedex ID", accessor: "fedex_id" },
    { Header: "Phone", accessor: "phone" },
    { Header: "Email", accessor: "email" },
    { Header: "Position", accessor: "position" },
    { Header: "Drug Test", accessor: "status" },
    { Header: "Existing MEC", accessor: "vLink" },
    { Header: "CV", accessor: "video" },
    { Header: "Notes", accessor: "notes" },
    { Header: "Ready To Send?", accessor: "ready_to_send" },
];

const formatDrugTestLabel = (value = "") => {
    const raw = String(value ?? "").trim();
    if (!raw) return "";
    return raw
        .split(/\s+/)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(" ");
};

const strOrNull = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized === "" ? null : normalized;
};

const flattenSectionRows = (sections = []) =>
    (Array.isArray(sections) ? sections : []).flatMap((section = {}) => {
        const sectionRows = Array.isArray(section?.rows) ? section.rows : [];
        return sectionRows.map((row = {}) => ({
            ...row,
            sectionKey: section?.sectionKey ?? "",
            sectionTitle: section?.sectionTitle ?? "",
        }));
    });

const extractFilterResponse = (response = {}) => {
    const responseData = response?.data ?? response ?? {};
    const nestedData =
        responseData?.data && typeof responseData.data === "object"
            ? responseData.data
            : responseData;

    const sections = Array.isArray(nestedData?.sections)
        ? nestedData.sections
        : Array.isArray(responseData?.sections)
            ? responseData.sections
            : [];

    const rowsFromSections = flattenSectionRows(sections);
    const rows =
        rowsFromSections.length > 0
            ? rowsFromSections
            : Array.isArray(nestedData?.rows)
                ? nestedData.rows
                : Array.isArray(responseData?.rows)
                    ? responseData.rows
                    : [];

    const statusMessage =
        sections
            .map((section) => String(section?.sectionTitle ?? "").trim())
            .filter(Boolean)
            .join(" · ") ||
        nestedData?.statusMessage ||
        responseData?.statusMessage ||
        "";

    return {
        rows,
        sections,
        clientId: nestedData?.clientId ?? responseData?.clientId ?? "",
        mailSourceId: nestedData?.mailSourceId ?? responseData?.mailSourceId ?? "",
        reportType: nestedData?.reportType ?? responseData?.reportType ?? "",
        toDate: nestedData?.toDate ?? responseData?.toDate ?? "",
        statusMessage,
    };
};

const mapRowsFromApi = (rows = []) =>
    (Array.isArray(rows) ? rows : []).map((item = {}, index) => {
        const drugTestStatus =
            item?.drugTest ??
            item?.drugTestStatus ??
            item?.drugTestResult ??
            item?.status ??
            "";
        const drugTestLabel = formatDrugTestLabel(drugTestStatus);

        const specialNotes = String(item?.specialNotes ?? item?.specialNote ?? "").trim();

        return {
            ...item,
            id: item?.candidateClientId ?? item?.id ?? item?._id ?? `row-${index}`,
            candidateClientId: item?.candidateClientId ?? "",
            name: item?.candidateFullName ?? item?.name ?? item?.candidateName ?? "",
            specialNotes,
            hasSpecialNote: Boolean(
                item?.hasSpecialNote ?? item?.isSpecialNote ?? specialNotes
            ),
            fedex_id: item?.fedexId ?? item?.fedex_id ?? item?.fedexID ?? "",
            phone: phoneNumberShowFormat(item?.candidatePhone ?? item?.phone ?? ""),
            email: item?.candidateEmail ?? item?.email ?? "",
            position: item?.jobName ?? item?.position ?? item?.jobTitle ?? "",
            status: drugTestLabel,
            statusLabel: drugTestLabel,
            vLink:
                item?.existingMecUrl ??
                item?.existingMecLink ??
                item?.existing_mec ??
                item?.existingMec ??
                "",
            video: item?.cvUrl ?? item?.cvLink ?? item?.cv ?? "",
            notes: item?.notes ?? item?.note ?? "",
            ready_to_send:
                item?.readyToSend ??
                item?.ready_to_send ??
                item?.isReadyToSend ??
                "",
        };
    });

const RTRInternalReport = () => {
    const [filterForm, setFilterForm] = useState({
        clientId: "",
        [getClientAsyncFilterLabelFieldKey()]: "",
        reportType: "",
        dateTo: "",
    });
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusMessage, setStatusMessage] = useState("");
    const [mailSourceOptions, setMailSourceOptions] = useState([]);
    const [emailFrom, setEmailFrom] = useState(null);
    const [preferredMailSourceId, setPreferredMailSourceId] = useState("");
    const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
    const [schedulingMail, setSchedulingMail] = useState(false);

    const filteredData = useMemo(() => reportData, [reportData]);
    const hasReportRows = useMemo(
        () => Array.isArray(reportData) && reportData.length > 0,
        [reportData]
    );

    const resolveRowCandidateClientId = useCallback(
        (row = {}) =>
            String(row?.candidateClientId ?? row?.id ?? "").trim(),
        []
    );

    useEffect(() => {
        const loadMailSourceOptions = async () => {
            const response = await fetchReportMailSourceOptions();
            if (response == null || response?.success === false) {
                setMailSourceOptions([]);
                return;
            }
            setMailSourceOptions(mapReportMailSourceOptions(response));
        };
        loadMailSourceOptions();
    }, []);

    useEffect(() => {
        if (!mailSourceOptions.length) {
            setEmailFrom(null);
            return;
        }
        const preferredId = strOrNull(preferredMailSourceId);
        const matchedOption = preferredId
            ? mailSourceOptions.find((option) => String(option.value) === preferredId)
            : null;
        setEmailFrom(matchedOption ?? mailSourceOptions[0]);
    }, [mailSourceOptions, preferredMailSourceId]);

    const handleSelectionChange = useCallback((selectedIds = []) => {
        setSelectedCandidateIds(
            Array.isArray(selectedIds)
                ? selectedIds.map((id) => String(id).trim()).filter(Boolean)
                : []
        );
    }, []);

    const handleNotesBlur = useCallback((row = {}, notesValue = "") => {
        const rowId = resolveRowCandidateClientId(row);
        if (!rowId) return;
        setReportData((previousRows) =>
            previousRows.map((item) =>
                resolveRowCandidateClientId(item) === rowId
                    ? { ...item, notes: String(notesValue ?? "") }
                    : item
            )
        );
    }, [resolveRowCandidateClientId]);

    const buildFilterPayload = () => ({
        clientId: strOrNull(filterForm?.clientId),
        dateTo: normalizeMetadataFilterDateForApi(
            filterForm?.dateTo || dayjs().format("YYYY-MM-DD")
        ),
        reportType: strOrNull(filterForm?.reportType),
    });

    const getRtrInternalReportList = async () => {
        setLoading(true);
        setSelectedCandidateIds([]);
        try {
            const response = await submitRtrInternalReportFilterData(buildFilterPayload());
            const {
                rows,
                mailSourceId: responseMailSourceId,
                reportType: responseReportType,
                toDate: responseToDate,
                statusMessage: responseStatusMessage,
            } = extractFilterResponse(response);

            if (response == null || response?.success === false) {
                setReportData([]);
                setStatusMessage("");
                setPreferredMailSourceId("");
                if (response?.message) {
                    toast.error(response.message);
                }
                return;
            }

            setFilterForm((previous) => ({
                ...previous,
                ...(responseReportType
                    ? { reportType: String(responseReportType) }
                    : {}),
                ...(responseToDate ? { dateTo: String(responseToDate) } : {}),
            }));

            setPreferredMailSourceId(strOrNull(responseMailSourceId) || "");
            setStatusMessage(String(responseStatusMessage || "").trim());
            setReportData(mapRowsFromApi(rows));
        } catch (error) {
            toast.error(error?.message || "Unable to load RTR internal report");
            setReportData([]);
            setStatusMessage("");
            setPreferredMailSourceId("");
            setSelectedCandidateIds([]);
        } finally {
            setLoading(false);
        }
    };

    const handleClientChange = (option) => {
        setFilterForm((previous) =>
            applyClientAsyncFilterOptionToFormState(previous, option)
        );
    };

    const buildScheduleItems = useCallback(
        (bucket = "eligible") => {
            const selectedIdSet = new Set(selectedCandidateIds);
            return (Array.isArray(reportData) ? reportData : [])
                .filter(
                    (row) =>
                        resolveSectionBucket(row?.sectionKey ?? "eligible") === bucket
                )
                .map((row) => {
                    const candidateClientId = resolveRowCandidateClientId(row);
                    return {
                        candidateClientId,
                        isSelected: selectedIdSet.has(candidateClientId),
                        notes: String(row?.notes ?? ""),
                    };
                });
        },
        [reportData, selectedCandidateIds, resolveRowCandidateClientId]
    );

    const buildSchedulePayload = () => ({
        reportType: strOrNull(filterForm?.reportType),
        clientId: strOrNull(filterForm?.clientId),
        toDate: normalizeMetadataFilterDateForApi(
            filterForm?.dateTo || dayjs().format("YYYY-MM-DD")
        ),
        mailSourceId: strOrNull(emailFrom?.value),
        submitType: DEFAULT_SCHEDULE_SUBMIT_TYPE,
        eligibleItems: buildScheduleItems("eligible"),
        fedexDriverItems: buildScheduleItems("fedexDriver"),
    });

    const handleScheduleMail = async () => {
        const mailSourceId = strOrNull(emailFrom?.value);

        if (!mailSourceId) {
            toast.error("Mail source is required");
            return;
        }
        if (!hasReportRows) {
            toast.error("No candidates available to schedule");
            return;
        }
        if (!selectedCandidateIds.length) {
            toast.error("Please select at least one candidate");
            return;
        }

        const swalResult = await Swal.fire({
            title: "Schedule mail?",
            text: "Are you sure you want to schedule mail for the selected candidates?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "OK",
            cancelButtonText: "Cancel",
            reverseButtons: true,
        });
        if (!swalResult?.isConfirmed) return;

        setSchedulingMail(true);
        try {
            const response = await submitRtrInternalReportScheduleData(
                buildSchedulePayload()
            );

            if (response == null || response?.success === false) {
                toast.error(response?.message || "Unable to schedule mail");
                return;
            }

            toast.success(response?.message || "Mail scheduled successfully");
            setSelectedCandidateIds([]);
            await getRtrInternalReportList();
        } catch (error) {
            toast.error(error?.message || "Unable to schedule mail");
        } finally {
            setSchedulingMail(false);
        }
    };

    return (
        <React.Fragment>
            <div className="page-content mail-report-page">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <h5 className="fw-bold mb-3">RTR Internal Report</h5>
                            <hr />

                            <div className="upper-filter-bar manage-user-filter-strip">
                                <Row className="g-2 g-md-3 align-items-end">
                                    <Col xs="12" md="6" lg={5}>
                                        <span className="manage-user-filter-label">Client</span>
                                        <AsyncClientSelect
                                            key={`rtr-internal-report-client-${filterForm?.clientId || ""}`}
                                            pageName="ReportClientType"
                                            value={filterForm?.clientId || ""}
                                            initialOption={buildClientAsyncFilterInitialOption(
                                                filterForm?.clientId,
                                                filterForm[getClientAsyncFilterLabelFieldKey()]
                                            )}
                                            onChange={handleClientChange}
                                            placeholder="Select Client"
                                            className="select2-selection w-100"
                                        />
                                    </Col>
                                    <Col xs="12" md="auto">
                                        <span className="manage-user-filter-label" aria-hidden="true">
                                            &nbsp;
                                        </span>
                                        <Button
                                            color="primary"
                                            type="button"
                                            disabled={loading}
                                            onClick={getRtrInternalReportList}
                                        >
                                            <i className="fa fa-search me-1" aria-hidden="true" />
                                            Search
                                        </Button>
                                    </Col>
                                </Row>
                            </div>

                            <div className="upper-filter-bar manage-user-filter-strip mail-report-toolbar">
                                <Row className="g-2 g-md-3 align-items-end">
                                    <Col xs="12" md="6" lg={5}>
                                        <span className="manage-user-filter-label">Mail</span>
                                        <Select
                                            className="select2-selection"
                                            options={mailSourceOptions}
                                            value={emailFrom}
                                            onChange={setEmailFrom}
                                            placeholder="Select mail source"
                                            isSearchable={false}
                                            isDisabled={!mailSourceOptions.length}
                                            menuPortalTarget={
                                                typeof document !== "undefined" ? document.body : null
                                            }
                                            menuPosition="fixed"
                                            styles={selectMenuStyles}
                                        />
                                    </Col>
                                    <Col xs="12" md="auto">
                                        <span className="manage-user-filter-label" aria-hidden="true">
                                            &nbsp;
                                        </span>
                                        <Button
                                            color="primary"
                                            type="button"
                                            disabled={schedulingMail || loading || !hasReportRows}
                                            onClick={handleScheduleMail}
                                        >
                                            <i className="fa fa-envelope me-1" aria-hidden="true" />
                                            {schedulingMail ? "Scheduling…" : "Schedule Mail"}
                                        </Button>
                                    </Col>
                                </Row>
                            </div>

                            {statusMessage ? (
                                <div className="mail-report-status-message" role="status">
                                    {statusMessage}
                                </div>
                            ) : null}

                            {loading ? (
                                <Loaders />
                            ) : (
                                <CommonTable
                                    columns={TABLE_COLUMNS}
                                    data={filteredData}
                                    isActionRequired={false}
                                    enableRowSelection
                                    isCheckboxLeftSide
                                    pageName="RtrInternalReport"
                                    resolveRowId={resolveRowCandidateClientId}
                                    onSelectionChange={handleSelectionChange}
                                    onNotesBlur={handleNotesBlur}
                                />
                            )}
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    );
};

RTRInternalReport.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(RTRInternalReport);
